import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PostAddIcon from "@mui/icons-material/PostAdd";

import Panel from "../../components/brand/Panel";
import KpiTile from "../../components/brand/KpiTile";
import { getPropertyDetails } from "../../api/propertyApi";
import {
  deleteBudget,
  generateCurrentInvoices,
  generateOwnerInvoicePdf,
  getBudget,
  getBudgets,
  getCurrentInvoicePreview,
  getPropertyFinanceSummary,
  postAllCurrentDraftInvoices,
  postOwnerInvoice,
  updateBudgetStatus,
} from "../../api/financeApi";
import { getApiErrorMessage, isConcurrencyConflict } from "../../utils/concurrency";
import BudgetMatrix from "./BudgetMatrix";
import FiscalYearsPanel from "./FiscalYearsPanel";
import ServiceChargeMechanismPanel from "./ServiceChargeMechanismPanel";
import ServiceChargeRecalculationPanel from "./ServiceChargeRecalculationPanel";
import OwnerInvoicePreviewPanel from "./OwnerInvoicePreviewPanel";
import BankAccountsPanel from "./BankAccountsPanel";
import BankReconciliationPanel from "./BankReconciliationPanel";
import YearEndPackPanel from "./YearEndPackPanel";
import PurchaseOrdersPanel from "./PurchaseOrdersPanel";
import SupplierBillsPanel from "./SupplierBillsPanel";
import AgedPayablesPanel from "./AgedPayablesPanel";
import CreateBudgetWizard from "./CreateBudgetWizard";
import LegalWordingPanel from "./LegalWordingPanel";
import { invoiceLifecycle, unitRowActions } from "./invoiceLifecycle";

const BASE_FINANCE_TABS = [
  { key: "overview", label: "Overview" },
  { key: "fiscal-years", label: "Fiscal Years" },
  { key: "budgets", label: "Budgets" },
  { key: "invoices", label: "Owner invoices" },
  { key: "bank", label: "Bank" },
  { key: "reconciliation", label: "Reconciliation" },
  { key: "year-end", label: "Year-end" },
  { key: "legal", label: "Legal" },
];

const SPEND_TABS = [
  { key: "purchase-orders", label: "Purchase orders" },
  { key: "supplier-bills", label: "Supplier bills" },
];

const AGED_PAYABLES_TAB = { key: "aged-payables", label: "Aged payables" };

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

const PropertyFinanceWorkspace = forwardRef(function PropertyFinanceWorkspace(
  { propertyId, showPageHeading = false, onPropertyLoaded, includeSpendTabs = false },
  ref
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const onPropertyLoadedRef = useRef(onPropertyLoaded);
  onPropertyLoadedRef.current = onPropertyLoaded;

  const financeTabs = useMemo(() => {
    const invoicesIndex = BASE_FINANCE_TABS.findIndex((tab) => tab.key === "invoices");
    return [
      ...BASE_FINANCE_TABS.slice(0, invoicesIndex + 1),
      ...(includeSpendTabs ? SPEND_TABS : []),
      AGED_PAYABLES_TAB,
      ...BASE_FINANCE_TABS.slice(invoicesIndex + 1),
    ];
  }, [includeSpendTabs]);

  const tabIndex = useMemo(
    () => financeTabs.reduce((acc, tab, index) => ({ ...acc, [tab.key]: index }), {}),
    [financeTabs]
  );

  const rawTabKey = searchParams.get("tab") || "overview";
  const requestedTabKey = rawTabKey === "apportionments" ? "budgets" : rawTabKey;
  const activeTab = tabIndex[requestedTabKey] ?? 0;
  const activeTabKey = financeTabs[activeTab]?.key || "overview";
  const budgetIdParam = searchParams.get("budgetId");
  const openedFromApportionments = useRef(searchParams.get("tab") === "apportionments");
  const budgetDirtyRef = useRef(false);
  const navigate = useNavigate();
  const [editingBudgetId, setEditingBudgetId] = useState(budgetIdParam || "");

  const [property, setProperty] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recalcRefreshKey, setRecalcRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const [budgets, setBudgets] = useState([]);
  const [budgetsPage, setBudgetsPage] = useState(1);
  const [selectedBudget, setSelectedBudget] = useState(null);

  const [generatingPdfId, setGeneratingPdfId] = useState(null);
  const [generatingMissingPdfs, setGeneratingMissingPdfs] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [postAllConfirmOpen, setPostAllConfirmOpen] = useState(false);
  const [postingAll, setPostingAll] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [freshDraftId, setFreshDraftId] = useState(null);
  const [budgetToDelete, setBudgetToDelete] = useState(null);
  const tabsRef = useRef(null);

  useLayoutEffect(() => {
    const revealSelectedTab = () => {
      const root = tabsRef.current;
      if (!root) return;
      const scroller = root.querySelector(".MuiTabs-scroller");
      const selected = root.querySelector('[role="tab"][aria-selected="true"]');
      if (!scroller || !selected || selected.offsetWidth === 0) return;
      const scrollerRect = scroller.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();
      const fullyVisible =
        selectedRect.left >= scrollerRect.left + 4 &&
        selectedRect.right <= scrollerRect.right - 4;
      if (fullyVisible) return;
      if (selectedRect.right > scrollerRect.right - 4) {
        scroller.scrollLeft += selectedRect.right - scrollerRect.right + 12;
      } else if (selectedRect.left < scrollerRect.left + 4) {
        scroller.scrollLeft -= scrollerRect.left - selectedRect.left + 12;
      }
    };
    revealSelectedTab();
    const frame = requestAnimationFrame(revealSelectedTab);
    const timer = setTimeout(revealSelectedTab, 300);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [activeTab, financeTabs]);

  useImperativeHandle(ref, () => ({
    openCreateWizard: () => setWizardOpen(true),
  }));

  const goTab = useCallback(
    (key, extra = {}) => {
      if (
        budgetDirtyRef.current
        && key !== "budgets"
        && !window.confirm("You have unsaved budget changes. Leave without saving?")
      ) {
        return;
      }
      const next = new URLSearchParams(searchParams);
      if (key === "overview") next.delete("tab");
      else next.set("tab", key);
      // budgetId belongs to the budget editor. Other tabs, including
      // apportionments, must not inherit the draft that was open there.
      const setsBudget = Object.prototype.hasOwnProperty.call(extra, "budgetId");
      if (key === "budgets") {
        if (!setsBudget && editingBudgetId) next.set("budgetId", String(editingBudgetId));
      } else if (!setsBudget) {
        next.delete("budgetId");
      }
      // po_year belongs to the purchase-order year filter.
      if (key !== "purchase-orders") next.delete("po_year");
      Object.entries(extra).forEach(([k, v]) => {
        if (v == null || v === "") next.delete(k);
        else next.set(k, String(v));
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams, editingBudgetId]
  );

  const loadCore = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [propertyData, summaryRes] = await Promise.all([
        getPropertyDetails(propertyId),
        getPropertyFinanceSummary(propertyId),
      ]);
      setProperty(propertyData);
      setSummary(summaryRes.data);
      onPropertyLoadedRef.current?.(propertyData);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load property finance."));
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (activeTabKey === "budgets") setEditingBudgetId(budgetIdParam || "");
  }, [activeTabKey, budgetIdParam]);

  useEffect(() => {
    setEditingBudgetId(budgetIdParam || "");
    setBudgets([]);
    setSelectedBudget(null);
    setInvoicePreview(null);
    setSummary(null);
    setFreshDraftId(null);
    setNotice("");
    setWizardOpen(false);
    setBudgetToDelete(null);
    // Only the property change should refresh this. budgetIdParam is the id in
    // the URL at that moment, including a draft opened from Work to do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  const loadBudgets = useCallback(async () => {
    const res = await getBudgets(propertyId, budgetsPage, { page_size: 100 });
    setBudgets(res.data.results || []);
  }, [propertyId, budgetsPage]);

  const loadBudgetDetail = useCallback(async (budgetId) => {
    try {
      const budgetRes = await getBudget(propertyId, budgetId);
      if (String(budgetRes.data?.property_id) !== String(propertyId)) {
        setSelectedBudget(null);
        return false;
      }
      setSelectedBudget(budgetRes.data);
      return true;
    } catch (e) {
      setSelectedBudget(null);
      return false;
    }
  }, [propertyId]);

  useEffect(() => {
    if (!freshDraftId || String(selectedBudget?.id) !== String(freshDraftId)) return;
    document.getElementById("finance-draft-budget")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [freshDraftId, selectedBudget]);

  useEffect(() => {
    if (searchParams.get("tab") !== "apportionments") return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", "budgets");
    const legacyId = next.get("apportionBudgetId");
    if (legacyId) next.set("budgetId", legacyId);
    next.delete("apportionBudgetId");
    navigate({ search: `?${next.toString()}`, hash: "who-pays" }, { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    if (activeTabKey === "budgets") {
      loadBudgets();
      if (budgetIdParam) {
        loadBudgetDetail(budgetIdParam).then((found) => {
          if (!found) goTab("budgets", { budgetId: "" });
        });
      } else setSelectedBudget(null);
    }
    if (activeTabKey === "invoices") {
      loadCore();
    }
  }, [
    activeTabKey,
    budgetIdParam,
    loadBudgets,
    loadBudgetDetail,
    goTab,
    loadCore,
    propertyId,
  ]);

  useEffect(() => {
    if (activeTabKey !== "budgets" || budgetIdParam || budgets.length === 0) return;
    goTab("budgets", { budgetId: budgets[0].id });
  }, [activeTabKey, budgetIdParam, budgets, goTab]);

  useEffect(() => {
    if (activeTabKey !== "budgets" || !selectedBudget?.id) return;
    if (Array.isArray(selectedBudget.sections)) return;
    loadBudgetDetail(selectedBudget.id);
  }, [activeTabKey, selectedBudget, loadBudgetDetail]);

  const handleBudgetStatus = async (action) => {
    try {
      const res = await updateBudgetStatus(propertyId, selectedBudget.id, {
        action,
        row_version: selectedBudget.row_version,
        visible_to_owners: selectedBudget.visible_to_owners,
      });
      setSelectedBudget(res.data);
      showToast(action === "publish" ? "Budget published." : "Budget approved.");
      loadBudgets();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to update budget status."));
    }
  };

  const handleOwnerVisibility = async (visibleToOwners) => {
    try {
      const res = await updateBudgetStatus(propertyId, selectedBudget.id, {
        action: "set_owner_visibility",
        row_version: selectedBudget.row_version,
        visible_to_owners: visibleToOwners,
      });
      setSelectedBudget(res.data);
      showToast(visibleToOwners ? "Budget shown to owners." : "Budget hidden from owners.");
      loadBudgets();
    } catch (e) {
      if (isConcurrencyConflict(e)) {
        setError("This budget was changed elsewhere. Reloading…");
        loadBudgetDetail(selectedBudget.id);
      } else {
        setError(getApiErrorMessage(e, "Failed to update owner visibility."));
      }
      throw e;
    }
  };

  const handleDeleteBudget = async (budget) => {
    const nextPage = budgets.length === 1 && budgetsPage > 1 ? budgetsPage - 1 : budgetsPage;

    try {
      setError("");
      await deleteBudget(propertyId, budget.id);

      const budgetsRes = await getBudgets(propertyId, nextPage, { page_size: 100 });
      const nextBudgets = budgetsRes.data.results || [];

      setBudgets(nextBudgets);
      if (nextPage !== budgetsPage) {
        setBudgetsPage(nextPage);
      }

      if (String(budget.id) === budgetIdParam) {
        const nextSelectedBudget = nextBudgets[0] || null;
        setSelectedBudget(null);
        goTab("budgets", { budgetId: nextSelectedBudget?.id ?? "" });
      }

      showToast("Budget deleted.");
      loadCore();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to delete budget."));
    }
  };

  const loadInvoicePreview = useCallback(async () => {
    if (!summary?.invoice_budget_ready) {
      setInvoicePreview(null);
      return;
    }
    try {
      setInvoicePreviewLoading(true);
      const res = await getCurrentInvoicePreview(propertyId);
      setInvoicePreview(res.data);
    } catch (e) {
      setInvoicePreview(null);
      setError(getApiErrorMessage(e, "Failed to load invoice preview."));
    } finally {
      setInvoicePreviewLoading(false);
    }
  }, [propertyId, summary?.invoice_budget_ready]);

  useEffect(() => {
    if (activeTabKey === "invoices" && summary?.invoice_budget_ready) {
      loadInvoicePreview();
    }
  }, [activeTabKey, summary?.invoice_budget_ready, loadInvoicePreview]);

  const handlePreviewInvoices = async () => {
    await loadInvoicePreview();
  };

  const handleGenerateInvoices = async () => {
    try {
      setGeneratingInvoices(true);
      await generateCurrentInvoices(propertyId, {
        invoice_date: new Date().toISOString().slice(0, 10),
      });
      showToast("Draft invoices created.");
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to generate invoices."));
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const invoiceBudgetReady = Boolean(summary?.invoice_budget_ready);
  const invoicesLifecycle = useMemo(
    () => invoiceLifecycle(invoicePreview),
    [invoicePreview]
  );

  const generatePdfs = useCallback(
    async (units, { openSingle = false } = {}) => {
      if (!units?.length) return;
      setError("");
      let lastDownloadUrl = null;
      for (const unit of units) {
        const invoiceId = unit.invoice_id || unit.id;
        setGeneratingPdfId(invoiceId);
        const res = await generateOwnerInvoicePdf(propertyId, invoiceId);
        lastDownloadUrl = res.data?.pdf_download_url || null;
      }
      setGeneratingPdfId(null);
      const count = units.length;
      showToast(
        count === 1
          ? `PDF generated for ${units[0].invoice_number}.`
          : `Generated ${count} invoice PDFs.`
      );
      if (openSingle && count === 1 && lastDownloadUrl) {
        window.open(lastDownloadUrl, "_blank", "noopener,noreferrer");
      }
      await loadInvoicePreview();
    },
    [propertyId, showToast, loadInvoicePreview]
  );

  const handlePostInvoice = async (invoice) => {
    const unit = (invoicePreview?.units || []).find((row) => row.invoice_id === invoice.id);
    if (unit && !unitRowActions(unit).canPost) {
      setError(
        unit.pdf_document_id
          ? "Regenerate the PDF so the invoice matches the current budget before posting."
          : "Generate a PDF and review it before posting this invoice."
      );
      return;
    }
    try {
      await postOwnerInvoice(propertyId, invoice.id, invoice.row_version);
      showToast(`Invoice ${invoice.invoice_number} posted to accounts.`);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to post invoice."));
    }
  };

  const handleGenerateInvoicePdf = async (invoice) => {
    try {
      await generatePdfs(
        [{ invoice_id: invoice.id, invoice_number: invoice.invoice_number }],
        { openSingle: true }
      );
    } catch (e) {
      setGeneratingPdfId(null);
      setError(getApiErrorMessage(e, "Failed to generate invoice PDF."));
    }
  };

  const handleGenerateMissingPdfs = async () => {
    if (!invoicesLifecycle.missingPdfs.length) return;
    try {
      setGeneratingMissingPdfs(true);
      await generatePdfs(invoicesLifecycle.missingPdfs);
    } catch (e) {
      setGeneratingPdfId(null);
      setError(getApiErrorMessage(e, "Failed to generate missing PDFs."));
    } finally {
      setGeneratingMissingPdfs(false);
    }
  };

  const handlePostAllConfirm = async () => {
    try {
      setPostingAll(true);
      setError("");
      const res = await postAllCurrentDraftInvoices(propertyId);
      const posted = res.data.posted_count ?? 0;
      const errors = res.data.errors || [];
      if (errors.length) {
        setError(
          `Posted ${posted} invoice${posted === 1 ? "" : "s"}. ${errors.length} could not be posted.`
        );
      }
      showToast(
        posted === 0
          ? "No drafts were ready to post."
          : `Posted ${posted} invoice${posted === 1 ? "" : "s"} to accounts.`
      );
      setPostAllConfirmOpen(false);
      loadInvoicePreview();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to post invoices."));
    } finally {
      setPostingAll(false);
    }
  };

  return (
    <Box>
      {showPageHeading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Finance
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {property?.name || "Property finance"}
          </Typography>
        </Box>
      )}

      {notice && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Tabs
        ref={tabsRef}
        value={activeTab}
        onChange={(_, idx) => goTab(financeTabs[idx].key)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3 }}
      >
        {financeTabs.map((t) => (
          <Tab key={t.key} label={t.label} />
        ))}
      </Tabs>

      {activeTabKey === "overview" && (
        <>
          {loading ? (
            <Skeleton height={200} />
          ) : (
            <>
              {summary?.service_charge_recalculation && (
                <Alert severity="warning" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                  {summary.service_charge_recalculation.reason ||
                    "Budget or apportionment changes require a service charge recalculation."}
                </Alert>
              )}
              {Boolean(
                summary?.current_fiscal_year_label ||
                  Number(summary?.budget_total) ||
                  Number(summary?.collected_ytd) ||
                  Number(summary?.spent_ytd) ||
                  Number(summary?.outstanding_ytd)
              ) && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <KpiTile label="Budget total" value={money(summary?.budget_total)} />
                  <KpiTile label="Collected YTD" value={money(summary?.collected_ytd)} />
                  <KpiTile label="Spent YTD" value={money(summary?.spent_ytd)} />
                  <KpiTile label="Outstanding" value={money(summary?.outstanding_ytd)} />
                </Box>
              )}
              <ServiceChargeMechanismPanel
                propertyId={propertyId}
                onMessage={showToast}
                onError={setError}
                onChanged={() => {
                  loadCore();
                  setRecalcRefreshKey((k) => k + 1);
                }}
              />
              <ServiceChargeRecalculationPanel
                propertyId={propertyId}
                refreshKey={recalcRefreshKey}
                onApplied={loadCore}
                onMessage={showToast}
                onError={setError}
              />
              <Panel title="Arrears" noBodyPadding={false}>
                {(summary?.arrears?.results || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No arrears over three months.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Unit</TableCell>
                          <TableCell>Owner</TableCell>
                          <TableCell align="right">Outstanding</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(summary?.arrears?.results || []).map((row) => (
                          <TableRow key={row.unit_id}>
                            <TableCell>
                              <RouterLink to={`/units/${row.unit_id}/statement`}>
                                {row.unit_name}
                              </RouterLink>
                            </TableCell>
                            <TableCell>{row.owner_name || "—"}</TableCell>
                            <TableCell align="right">{money(row.outstanding)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Panel>
            </>
          )}
        </>
      )}

      {activeTabKey === "budgets" && (
        <>
          {selectedBudget ? (
            <BudgetMatrix
              propertyId={propertyId}
              budget={selectedBudget}
              budgets={budgets}
              onSelectBudget={(id) => goTab("budgets", { budgetId: id })}
              onCreate={() => setWizardOpen(true)}
              onDelete={(budget) => setBudgetToDelete(budget)}
              onStatus={handleBudgetStatus}
              onOwnerVisibility={handleOwnerVisibility}
              onReload={async (id) => {
                await loadBudgetDetail(id);
                loadBudgets();
              }}
              showToast={showToast}
              setError={setError}
              showPrevious={searchParams.get("previous") === "1"}
              scrollToWhoPays={openedFromApportionments.current}
              onDirtyChange={(dirty) => {
                budgetDirtyRef.current = dirty;
              }}
              onTogglePrevious={() => {
                const next = new URLSearchParams(searchParams);
                if (next.get("previous") === "1") next.delete("previous");
                else next.set("previous", "1");
                setSearchParams(next);
              }}
            />
          ) : (
            <Panel title="Budget" noBodyPadding={false}>
              <Typography variant="body2" color="text.secondary">
                {budgets.length === 0
                  ? "A new budget opens here after you create it."
                  : "Select a budget to enter its amounts."}
              </Typography>
            </Panel>
          )}
          <Dialog open={Boolean(budgetToDelete)} onClose={() => setBudgetToDelete(null)}>
            <DialogTitle>Delete this draft?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {`Delete "${budgetToDelete?.name || "this budget"}"? This cannot be undone.`}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button startIcon={<CloseIcon />} onClick={() => setBudgetToDelete(null)}>
                Cancel
              </Button>
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const budget = budgetToDelete;
                  setBudgetToDelete(null);
                  if (budget) handleDeleteBudget(budget);
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {activeTabKey === "fiscal-years" && (
        <FiscalYearsPanel
          propertyId={propertyId}
          financialYearStartDay={property?.financial_year_start_day}
          financialYearStartMonth={property?.financial_year_start_month}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "invoices" && (
        <Panel title="Owner invoices" noBodyPadding={false}>
          <Box sx={{ mb: 2 }}>
            {summary?.current_fiscal_year_label ? (
              <Typography variant="body2" color="text.secondary">
                Current fiscal year: {summary.current_fiscal_year_label}
                {summary.current_budget_name
                  ? ` · Budget: ${summary.current_budget_name}${
                      summary.current_budget_status
                        ? ` (${summary.current_budget_status})`
                        : ""
                    }`
                  : ""}
              </Typography>
            ) : (
              <Alert severity="warning" sx={{ mb: 0 }}>
                Set a current fiscal year before generating invoices.
              </Alert>
            )}
            {summary?.current_fiscal_year_label && !invoiceBudgetReady && (
              <Alert severity="warning" sx={{ mt: 1, mb: 0 }}>
                {summary.current_budget_name
                  ? `The budget "${summary.current_budget_name}" must be approved before generating invoices.`
                  : "Create and approve a budget for the current fiscal year before generating invoices."}
              </Alert>
            )}
            {summary?.current_fiscal_year_label && !property?.service_charge_mechanism && (
              <Alert severity="warning" sx={{ mt: 1, mb: 0 }}>
                Choose a service charge mechanism on the Overview tab. Invoices need each unit's share of the estate charge, and that share is not set yet.
              </Alert>
            )}
          </Box>
          {property?.service_charge_mechanism && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <Button
                variant="contained"
                onClick={handleGenerateInvoices}
                disabled={
                  !invoiceBudgetReady ||
                  invoicePreviewLoading ||
                  !invoicesLifecycle.canGenerateDrafts ||
                  generatingInvoices
                }
                startIcon={
                  generatingInvoices ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <NoteAddIcon />
                  )
                }
              >
                Generate drafts
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setPostAllConfirmOpen(true)}
                disabled={
                  !invoiceBudgetReady ||
                  invoicePreviewLoading ||
                  !invoicesLifecycle.canPostAll ||
                  postingAll
                }
                startIcon={<PostAddIcon />}
              >
                Post all drafts
              </Button>
            </Box>
            {invoiceBudgetReady &&
              !invoicePreviewLoading &&
              invoicesLifecycle.allUnitsHaveInvoices && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  All units already have invoices.
                </Typography>
              )}
            {invoiceBudgetReady &&
              !invoicePreviewLoading &&
              invoicesLifecycle.draftCount > 0 &&
              !invoicesLifecycle.canPostAll && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  Generate and review a PDF for each draft before posting.
                </Typography>
              )}
          </Box>
          )}
          {property?.service_charge_mechanism && (
          <OwnerInvoicePreviewPanel
            preview={invoicePreview}
            loading={invoicePreviewLoading}
            onRefresh={invoiceBudgetReady ? handlePreviewInvoices : null}
            onPostInvoice={handlePostInvoice}
            onGeneratePdf={handleGenerateInvoicePdf}
            generatingPdfId={generatingPdfId}
            onGenerateMissingPdfs={handleGenerateMissingPdfs}
            generatingMissingPdfs={generatingMissingPdfs}
          />
          )}
        </Panel>
      )}

      <Dialog
        open={postAllConfirmOpen}
        onClose={() => !postingAll && setPostAllConfirmOpen(false)}
      >
        <DialogTitle>Post all ready drafts?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Post ${invoicesLifecycle.readyToPostCount} draft invoice${
              invoicesLifecycle.readyToPostCount === 1 ? "" : "s"
            } to the accounts? Each charge will be owed by the owner and the amount cannot be changed.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<CloseIcon />}
            onClick={() => setPostAllConfirmOpen(false)}
            disabled={postingAll}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={
              postingAll ? <CircularProgress size={16} color="inherit" /> : <PostAddIcon />
            }
            onClick={handlePostAllConfirm}
            disabled={postingAll}
          >
            {postingAll ? "Posting…" : "Post to accounts"}
          </Button>
        </DialogActions>
      </Dialog>

      {includeSpendTabs && activeTabKey === "purchase-orders" && (
        <PurchaseOrdersPanel propertyId={propertyId} onMessage={showToast} />
      )}

      {includeSpendTabs && activeTabKey === "supplier-bills" && (
        <SupplierBillsPanel propertyId={propertyId} onMessage={showToast} />
      )}

      {activeTabKey === "aged-payables" && (
        <AgedPayablesPanel propertyId={propertyId} />
      )}

      {activeTabKey === "bank" && (
        <BankAccountsPanel
          propertyId={propertyId}
          property={property}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "reconciliation" && (
        <BankReconciliationPanel
          propertyId={propertyId}
          onMessage={showToast}
          onError={setError}
        />
      )}

      {activeTabKey === "year-end" && (
        <YearEndPackPanel key={propertyId} propertyId={propertyId} onError={setError} />
      )}

      {activeTabKey === "legal" && (
        <LegalWordingPanel
          key={propertyId}
          propertyId={propertyId}
          onMessage={showToast}
          onError={setError}
        />
      )}

      <CreateBudgetWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        propertyId={propertyId}
        propertyName={property?.name}
        financialYearStartDay={property?.financial_year_start_day}
        financialYearStartMonth={property?.financial_year_start_month}
        onSavedProgress={setNotice}
        onCreated={(budget) => {
          setFreshDraftId(budget.id);
          goTab("budgets", { budgetId: budget.id });
          showToast(
            "This budget is a draft. Enter the amounts, then send it for approval when it is ready."
          );
          loadBudgets();
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

export default PropertyFinanceWorkspace;
